import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, ax, card, btn, inp, ImageUploadZone } from './adminHelpers';

export default function PricingTab() {
  const [addons, setAddons] = useState([]);
  const [form, setForm] = useState({ name: '', price: '', type: 'boolean', description: '', active: true });
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await ax().get(`${API}/api/admin/addons`);
      setAddons(data.addons || []);
    } catch (err) {
      console.error('Failed to load addons:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!form.name.trim()) return alert('Please enter a name');
    const fd = new FormData();
    fd.append('name', form.name);
    fd.append('price', parseFloat(form.price) || 0);
    fd.append('type', form.type);
    fd.append('description', form.description);
    fd.append('active', form.active);
    if (file) fd.append('image', file);

    try {
      if (editing) {
        await axios.patch(`${API}/api/admin/addons/${editing}`, fd, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/admin/addons`, fd, { withCredentials: true });
      }
      setEditing(null);
      setForm({ name: '', price: '', type: 'boolean', description: '', active: true });
      setFile(null);
      load();
    } catch (err) {
      alert('Failed to save addon');
    }
  };

  const del = async (id) => {
    if (window.confirm('Are you sure you want to permanently delete this add-on?')) {
      try {
        await ax().delete(`${API}/api/admin/addons/${id}`);
        load();
      } catch (err) {
        alert('Failed to delete addon');
      }
    }
  };

  const edit = (a) => {
    setEditing(a.id);
    setForm({
      name: a.name,
      price: parseFloat(a.price) || 0,
      type: a.type,
      description: a.description || '',
      active: a.active !== false
    });
    setFile(null);
  };

  return (
    <div>
      <div style={{ padding: '12px 16px', background: 'rgba(159,92,255,0.08)', borderLeft: '4px solid #9F5CFF', borderRadius: '4px 8px 8px 4px', marginBottom: 24, fontSize: 13, color: '#d0d0e0', lineHeight: 1.5 }}>
        <strong style={{ color: '#9F5CFF', display: 'block', marginBottom: 4, fontSize: 14 }}>✨ Dynamic Add-ons Pricing & Behaviors Manager</strong>
        Configure add-on options that customers can choose in the booking wizard.
        <ul style={{ margin: '6px 0 0', paddingLeft: 20, color: '#a0a0b8' }}>
          <li><strong>Checkbox:</strong> Flat rate added one-time when selected (e.g. Scrapbook, Red Carpet).</li>
          <li><strong>Hour-based:</strong> Charged per hour (e.g. Extra Hours).</li>
          <li><strong>Quantity-based:</strong> Charged per individual unit/quantity chosen.</li>
        </ul>
      </div>

      <div style={card}>
        <h3 style={{ margin: '0 0 16px', color: '#9F5CFF', fontSize: 16, fontWeight: 700 }}>
          {editing ? '📝 Edit Add-on Option' : '➕ Add New Add-on Option'}
        </h3>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
          <div style={{ flex: '2 1 200px' }}>
            <label style={{ display: 'block', fontSize: 12, color: '#a0a0b8', marginBottom: 6 }}>Add-on Name/Label</label>
            <input
              placeholder="e.g. Premium Scrapbook Album"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              style={inp(false)}
            />
          </div>
          
          <div style={{ flex: '1 1 120px' }}>
            <label style={{ display: 'block', fontSize: 12, color: '#a0a0b8', marginBottom: 6 }}>Base Price ($)</label>
            <input
              placeholder="0.00"
              type="number"
              value={form.price}
              onChange={e => setForm({ ...form, price: e.target.value })}
              style={inp(false)}
            />
          </div>

          <div style={{ flex: '1 1 150px' }}>
            <label style={{ display: 'block', fontSize: 12, color: '#a0a0b8', marginBottom: 6 }}>Add-on Type</label>
            <select
              value={form.type}
              onChange={e => setForm({ ...form, type: e.target.value })}
              style={{
                ...inp(false),
                background: '#1a1f38',
                color: '#fff',
                cursor: 'pointer'
              }}
            >
              <option value="boolean">Checkbox (Flat Price)</option>
              <option value="hourly">Hour-based (Price/Hour)</option>
              <option value="quantity">Quantity-based (Price/Unit)</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, color: '#a0a0b8', marginBottom: 6 }}>Description (Optional)</label>
          <textarea
            placeholder="Add brief details for this add-on to show in the booking wizard..."
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            style={{
              ...inp(false),
              height: 60,
              resize: 'vertical',
              fontFamily: 'inherit',
              padding: '10px 14px'
            }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <ImageUploadZone label="Add-on Display Image" file={file} onChange={e => setFile(e.target.files[0])} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <input
            type="checkbox"
            id="addon-active"
            checked={form.active}
            onChange={e => setForm({ ...form, active: e.target.checked })}
            style={{ width: 16, height: 16, cursor: 'pointer' }}
          />
          <label htmlFor="addon-active" style={{ fontSize: 13, color: '#e0e0e0', cursor: 'pointer' }}>
            Active (Display this add-on in the booking wizard)
          </label>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={save} style={btn('#9F5CFF')}>💾 Save Add-on</button>
          {editing && (
            <button
              onClick={() => {
                setEditing(null);
                setForm({ name: '', price: '', type: 'boolean', description: '', active: true });
                setFile(null);
              }}
              style={btn('#555', true)}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <div style={card}>
        <h4 style={{ margin: '0 0 16px', color: '#E34364', fontSize: 15, fontWeight: 700 }}>
          Configured Add-ons ({addons.length})
        </h4>
        
        {loading ? (
          <p style={{ color: '#a0a0b8', fontSize: 13 }}>Loading addons…</p>
        ) : addons.length === 0 ? (
          <p style={{ color: '#a0a0b8', fontSize: 13 }}>No dynamic add-ons configured yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {addons.map(a => (
              <div
                key={a.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '14px 18px',
                  background: a.active ? 'rgba(255,255,255,0.02)' : 'rgba(227,67,100,0.03)',
                  borderRadius: 12,
                  border: `1px solid ${a.active ? 'rgba(255,255,255,0.05)' : 'rgba(227,67,100,0.15)'}`
                }}
              >
                <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                  {a.image_url ? (
                    <img src={a.image_url} alt={a.name} style={{ width: 50, height: 50, borderRadius: 8, objectFit: 'cover', border: '1px solid rgba(255,255,255,0.08)' }} />
                  ) : (
                    <div style={{ width: 50, height: 50, borderRadius: 8, background: 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: '#a0a0b8' }}>📦</div>
                  )}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: '#fff', fontSize: 14, fontWeight: 700 }}>{a.name}</span>
                    <span
                      style={{
                        fontSize: 10,
                        background: 'rgba(159,92,255,0.15)',
                        color: '#9F5CFF',
                        padding: '2px 8px',
                        borderRadius: 50,
                        fontWeight: 600,
                        textTransform: 'capitalize'
                      }}
                    >
                      {a.type === 'boolean' ? 'Flat rate' : a.type === 'hourly' ? 'Hour-based' : 'Quantity-based'}
                    </span>
                    {!a.active && (
                      <span
                        style={{
                          fontSize: 10,
                          background: 'rgba(227,67,100,0.15)',
                          color: '#E34364',
                          padding: '2px 8px',
                          borderRadius: 50,
                          fontWeight: 600
                        }}
                      >
                        Inactive
                      </span>
                    )}
                  </div>
                </div>
                  {a.description && (
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#a0a0b8', lineHeight: 1.4 }}>
                      {a.description}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <span style={{ color: '#27C85A', fontWeight: 700, fontSize: 16 }}>
                    ${parseFloat(a.price).toFixed(2)}
                    <span style={{ fontSize: 11, fontWeight: 500, color: '#a0a0b8', marginLeft: 2 }}>
                      {a.type === 'hourly' ? '/hr' : a.type === 'quantity' ? '/unit' : ''}
                    </span>
                  </span>
                  <button onClick={() => edit(a)} style={{ ...btn('#9F5CFF', true), padding: '6px 12px', fontSize: 12 }}>Edit</button>
                  <button onClick={() => del(a.id)} style={{ ...btn('#E34364', true), padding: '6px 12px', fontSize: 12 }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
